import type { Graphics } from 'pixi.js';
import { INK, PAPER, SHADOW, lerpColor } from './color';
import { BOIL_AMP, LIGHT_ANGLE, boilFrame, hatchChords, litOffset, ringPath } from './ink';
import { INK_WIDTH, shapePoints } from './view';
import type { Body, Kit, World } from './world';

export type { Body } from './world';

export interface Dress {
  begin(unit: number, time: number, reduced: boolean): void;
  readonly outline: number;
  incoming(body: Body, alpha: number, thick: number): void;
  rim(body: Body, alpha: number, width: number, seed: number): void;
  mine(body: Body, alpha: number, width: number, fill: number, seed: number): void;
  target(body: Body, alpha: number, spread: number, current: boolean): void;
  pip(x: number, y: number, radius: number, on: boolean, last: boolean, alpha: number): void;
  path(points: readonly number[], count: number, alpha: number): void;
}

const TAU = Math.PI * 2;
const BANDS = [-0.42, 0.08, 0.5];

export function trace(g: Graphics, body: Body, dx = 0, dy = 0, grow = 1): void {
  const cx = body.x + dx;
  const cy = body.y + dy;
  const r = body.radius * grow;
  if (body.shape === 'circle') {
    g.ellipse(cx, cy, r * body.scaleX, r * body.scaleY);
    return;
  }
  const points = shapePoints(body.shape, 0, 0, r, body.rotation);
  for (let i = 0; i < points.length; i += 2) {
    points[i] = cx + (points[i] as number) * body.scaleX;
    points[i + 1] = cy + (points[i + 1] as number) * body.scaleY;
  }
  g.poly(points, true);
}

export function createDress(g: Graphics, look: World): Dress {
  const pal = look.palette;
  const outer: number[] = [];
  const inner: number[] = [];
  const chords: number[] = [];
  const outline = look.dark ? pal.rim : pal.ink;
  const fillAlpha = look.fillAlpha;
  const wear = look.dress;
  let unit = 1;
  let time = 0;
  let frame = 0;
  let reduced = false;

  const hatch = (body: Body, angle: number, spacing: number, depth: number, color: number, alpha: number, width: number): void => {
    const lit = depth > 0 ? litOffset(body.radius, depth) : null;
    const n = hatchChords(chords, body.radius, angle, spacing, lit?.dx ?? 0, lit?.dy ?? 0, lit?.r ?? 0);
    if (n === 0) return;
    for (let i = 0; i < n * 4; i += 4) {
      g.moveTo(body.x + chords[i]! * body.scaleX, body.y + chords[i + 1]! * body.scaleY).lineTo(
        body.x + chords[i + 2]! * body.scaleX,
        body.y + chords[i + 3]! * body.scaleY,
      );
    }
    g.stroke({ width, color, alpha, cap: 'butt' });
  };

  const arcLine = (body: Body, radius: number, a0: number, a1: number): void => {
    const steps = 14;
    for (let i = 0; i <= steps; i++) {
      const a = a0 + ((a1 - a0) * i) / steps;
      const x = body.x + Math.cos(a) * radius * body.scaleX;
      const y = body.y + Math.sin(a) * radius * body.scaleY;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
  };

  const kit: Kit = {
    g,
    pal,
    outline,
    get unit() {
      return unit;
    },
    get time() {
      return time;
    },
    get reduced() {
      return reduced;
    },
    get frame() {
      return frame;
    },
    trace(body, dx, dy, grow) {
      trace(g, body, dx, dy, grow);
    },
    inkRing(body, width, seed, amp, color, alpha) {
      ringPath(outer, inner, body.x, body.y, body.radius * body.scaleX, body.radius * body.scaleY, width, seed, frame, amp);
      const n = outer.length;
      for (let i = 0; i < n; i += 2) {
        const j = (i + 2) % n;
        g.moveTo(outer[i]!, outer[i + 1]!)
          .lineTo(outer[j]!, outer[j + 1]!)
          .lineTo(inner[j]!, inner[j + 1]!)
          .lineTo(inner[i]!, inner[i + 1]!)
          .closePath();
      }
      g.fill({ color, alpha });
    },
    hatch,
    highlight(body, alpha) {
      const r = body.radius;
      g.ellipse(body.x - 0.4 * r * body.scaleX, body.y - 0.42 * r * body.scaleY, 0.14 * r * body.scaleX, 0.1 * r * body.scaleY).fill({
        color: PAPER,
        alpha,
      });
      g.circle(body.x - 0.2 * r * body.scaleX, body.y - 0.55 * r * body.scaleY, 0.05 * r).fill({ color: PAPER, alpha: alpha * 0.8 });
    },
    arcLine,
    rimLight(body, alpha) {
      arcLine(body, body.radius * 0.88, LIGHT_ANGLE - 0.9, LIGHT_ANGLE + 0.9);
      g.stroke({ width: Math.max(1, body.radius * 0.06), color: PAPER, alpha, cap: 'round' });
    },
    bands(body, color, alpha) {
      for (const k of BANDS) {
        const half = Math.sqrt(1 - k * k) * body.radius * 0.96;
        const y = body.y + k * body.radius * body.scaleY;
        g.moveTo(body.x - half * body.scaleX, y).lineTo(body.x + half * body.scaleX, y);
      }
      g.stroke({ width: body.radius * 0.11 * body.scaleY, color, alpha, cap: 'butt' });
    },
    terminator(body, color, alpha) {
      const spacing = Math.max(1.5, body.radius * 0.08);
      hatch(body, Math.PI / 4, spacing, 0.24, color, alpha, spacing * 1.08);
    },
    plain(body, fill, alpha, width) {
      trace(g, body);
      g.fill({ color: fill, alpha });
      g.stroke({ width, color: outline, alpha, join: 'round' });
    },
    ringDots(body, radius, count, spin, draw) {
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU + spin;
        draw(body.x + Math.cos(a) * radius, body.y + Math.sin(a) * radius, i, a);
      }
    },
  };

  return {
    outline,
    begin(u, t, r) {
      unit = u;
      time = t;
      reduced = r;
      frame = look.boil ? boilFrame(t, r) : 0;
    },

    incoming(body, alpha, thick) {
      if (alpha <= 0 || body.radius <= 0) return;
      const color = pal.incoming[body.shape];
      const edge = lerpColor(color, look.dark ? pal.ink : INK, 0.4);
      if (thick > 0.5) {
        trace(g, body, 0, thick);
        g.fill({ color: edge, alpha: fillAlpha * alpha });
      }
      trace(g, body);
      g.fill({ color, alpha: fillAlpha * alpha });
      if (body.shape !== 'circle') return;
      wear.incomingCircle(kit, body, color, alpha);
    },

    rim(body, alpha, width, seed) {
      if (alpha <= 0 || body.radius <= 0) return;
      if (look.boil && body.shape === 'circle') {
        kit.inkRing(body, width, seed, reduced ? 0 : BOIL_AMP, outline, alpha);
        return;
      }
      trace(g, body);
      g.stroke({ width, color: outline, alpha, join: 'round' });
    },

    mine(body, alpha, width, fill, seed) {
      if (alpha <= 0 || body.radius <= 0) return;
      trace(g, body, 0.014 * unit, 0.02 * unit);
      g.fill(look.dark ? { color: 0x000000, alpha: 0.35 * alpha } : { color: SHADOW, alpha });
      if (body.shape !== 'circle') {
        kit.plain(body, fill, alpha, width);
        return;
      }
      wear.mineCircle(kit, body, alpha, width, fill, seed);
    },

    target(body, alpha, spread, current) {
      if (alpha <= 0 || body.radius <= 0) return;
      const reach = body.radius * (1 + spread) + unit * 0.035;
      const spin = reduced ? 0 : time * (current ? 0.3 : 0.1);
      wear.target(kit, body, alpha, spread, reach, spin, INK_WIDTH * unit * 0.55);
    },

    pip(x, y, radius, on, last, alpha) {
      wear.pip(kit, x, y, radius, on, last, alpha, INK_WIDTH * unit * 0.45);
    },

    path(points, count, alpha) {
      if (count < 4) return;
      wear.path(kit, points, count, alpha);
    },
  };
}
